import {
	FunctionComponent,
	MutableRefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { BlockerFunction, useBlocker } from "react-router-dom";

import { Modal } from "@/components";

/** "settled" once the user decided something else while a save runs; that save must not leave. */
type PendingSave = "idle" | "saving" | "settled";

/** Read after an await: the compiler keeps the narrowing from before it, the user may not have. */
const wasSettled = (ref: MutableRefObject<PendingSave>) => ref.current === "settled";

export interface LeaveGuardProps {
	/** True while the screen has unsaved changes. */
	when: boolean;
	/** Saves the screen; resolve true when the navigation may proceed. */
	onSave: () => Promise<boolean>;
	/** Throws the screen's changes away. */
	onDiscard: () => void;
	title: string;
	body: string;
	saveLabel: string;
	discardLabel: string;
}

/**
 * Stops a user leaving unsaved work behind, and offers the three honest choices: stay, discard and
 * leave, or save and leave (PREC-4055). One component for every screen, so the wording and the
 * buttons never drift — the copy is passed in, the behaviour is not.
 *
 * It covers **both** ways out. `useBlocker` catches in-app navigation, which is the common case and
 * the only one a router can see; `beforeunload` catches closing the tab or typing a new address,
 * where the browser shows its own generic dialog and we can only ask for it. A guard that handles
 * one and not the other is the more dangerous shape, because it teaches people the screen is safe
 * (PREC-4194).
 *
 * Only leaving the page is blocked: a navigation that keeps the pathname and changes the query (the
 * campaign builder writes the selected campaign there) is not leaving. And a "Save and leave" whose
 * save is still running when the user cancels, discards or clicks again does not leave afterwards,
 * because React Router throws when proceed() runs on a blocker that is no longer blocked
 * (PREC-4430).
 */
export const LeaveGuard: FunctionComponent<LeaveGuardProps> = ({
	when,
	onSave,
	onDiscard,
	title,
	body,
	saveLabel,
	discardLabel,
}) => {
	const { t } = useTranslation();
	const leavesPage = useCallback<BlockerFunction>(
		({ currentLocation, nextLocation }) =>
			when && currentLocation.pathname !== nextLocation.pathname,
		[when]
	);
	const blocker = useBlocker(leavesPage);
	const [saving, setSaving] = useState(false);
	const saveRef = useRef<PendingSave>("idle");

	useEffect(() => {
		if (!when) {
			return;
		}

		const warn = (event: BeforeUnloadEvent) => {
			// The modern spelling and the legacy one: Chrome honours preventDefault(), older WebKit
			// still wants returnValue set. The string is never shown — every browser uses its own.
			event.preventDefault();
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", warn);

		return () => window.removeEventListener("beforeunload", warn);
	}, [when]);

	if (blocker.state !== "blocked") {
		return null;
	}

	const settlePendingSave = () => {
		if (saveRef.current === "saving") {
			saveRef.current = "settled";
		}
	};

	const stay = () => {
		settlePendingSave();
		blocker.reset();
	};

	const discardAndLeave = () => {
		settlePendingSave();
		onDiscard();
		blocker.proceed();
	};

	const saveAndLeave = async () => {
		if (saveRef.current !== "idle") {
			return;
		}

		saveRef.current = "saving";
		setSaving(true);

		try {
			const saved = await onSave();

			if (wasSettled(saveRef)) {
				return;
			}

			if (saved) {
				blocker.proceed();
			} else {
				blocker.reset();
			}
		} finally {
			saveRef.current = "idle";
			setSaving(false);
		}
	};

	return (
		<Modal
			cancelButtonText={t("basics.cancel")}
			handleClose={stay}
			handleSave={saveAndLeave}
			handleSecondSave={discardAndLeave}
			isLoading={saving}
			isOpened
			secondSubmitButtonText={discardLabel}
			size="sm"
			submitButtonText={saveLabel}
			title={title}
		>
			<p className="text-sm text-text-meta">{body}</p>
		</Modal>
	);
};
